import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faPlay, faStop, faSpinner, faSync,
  faCheckCircle, faExclamationTriangle, faQuestionCircle, faTable, faThLarge, faTrash,
  faTimes, faKey, faUserShield, faBolt, faUpRightAndDownLeftFromCenter, faDownLeftAndUpRightToCenter, faListUl, faFileImport
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from '../../config/axiosConfig';
import authSmashService from '../../services/authSmashService';
import authProfileService from '../../services/authProfileService';
import proxyService from '../../services/proxyService';
import webTargetService from '../../services/webTargetService';
import AuthSmashResultsTable from './AuthSmashResultsTable';
import AuthSmashMatrixTable from './AuthSmashMatrixTable';
import AuthSmashCellModal from './AuthSmashCellModal';
import AuthSmashScopeFilters from './AuthSmashScopeFilters';
import AuthSmashUrlSelector from './AuthSmashUrlSelector';
import ImportDialog from './ImportDialog';
import httpExchangeService from '../../services/httpExchangeService';
// Removed: AuthSmashAutoProfileNotification - now handled globally by GlobalAuthProfileNotifications
// import AuthSmashAutoProfileNotification from './AuthSmashAutoProfileNotification';
import AuthProfileDuplicateNotificationContainer from './AuthProfileDuplicateNotificationContainer';

// Explains what AuthSmash does and how to drive it. Opened from the "?" in the header.
const AuthSmashHelpModal = ({ isDarkMode, onClose }) => {
  const card = isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const heading = isDarkMode ? 'text-gray-100' : 'text-gray-900';

  const steps = [
    {
      icon: faKey,
      color: 'text-blue-400',
      title: '1 · Capture auth profiles',
      text: 'A profile is one identity (a set of cookies / tokens) for a role — e.g. Admin, User, or Unauthenticated. Log in through the proxy and AuthSmash can auto-create profiles, or add them manually under the Profiles tab.'
    },
    {
      icon: faUserShield,
      color: 'text-amber-400',
      title: '2 · Pick a baseline + test profiles',
      text: 'The baseline is your high-privilege identity (e.g. Admin) — it defines which endpoints are worth testing. Test profiles are the lower-privilege identities you want to check. Optionally include an unauthenticated test.'
    },
    {
      icon: faShieldAlt,
      color: 'text-indigo-400',
      title: '3 · Choose endpoints',
      text: 'Test the full site map, in-scope endpoints only, or a manual selection. In manual mode you can paste hundreds of URLs at once — they are validated live and saved to this project. Scope filters let you exclude static assets, etc.'
    },
    {
      icon: faBolt,
      color: 'text-green-400',
      title: '4 · Run the test',
      text: 'AuthSmash replays every selected endpoint with each profile\'s credentials and compares the responses. "Start Test" runs the batch now; "Start Live" records requests as you browse through the proxy.'
    },
    {
      icon: faCheckCircle,
      color: 'text-emerald-400',
      title: '5 · Read the matrix',
      text: 'Each cell is one endpoint × profile. Enforced = access correctly denied. Bypassed = a lower-privilege identity reached protected data (a likely broken-access-control finding). Partial = ambiguous, worth a manual look. Click any cell for the raw request/response.'
    }
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg border shadow-2xl ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 flex items-center justify-between px-4 py-3 border-b ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faShieldAlt} className="text-amber-400" />
            <span className={`text-sm font-semibold ${heading}`}>How AuthSmash™ works</span>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          <p className={`text-xs ${muted}`}>
            AuthSmash is an <span className="font-semibold">authorization (access-control) tester</span>.
            It answers one question: <em>can a low-privilege (or anonymous) user reach things only a
            higher-privilege user should?</em> It does that by replaying the same requests with different
            identities and diffing the results.
          </p>

          {steps.map((s, i) => (
            <div key={i} className={`flex gap-3 rounded-md border p-3 ${card}`}>
              <FontAwesomeIcon icon={s.icon} className={`${s.color} mt-0.5`} />
              <div>
                <div className={`text-xs font-semibold mb-0.5 ${heading}`}>{s.title}</div>
                <div className={`text-[11px] leading-relaxed ${muted}`}>{s.text}</div>
              </div>
            </div>
          ))}

          <div className={`rounded-md border p-3 text-[11px] leading-relaxed ${
            isDarkMode ? 'border-amber-800 bg-amber-900/10 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}>
            <strong>Tip:</strong> A "Bypassed" result is the interesting one — it usually means broken
            access control (IDOR / privilege escalation). Always confirm by opening the cell and inspecting
            the actual response before reporting it.
          </div>
        </div>
      </div>
    </div>
  );
};

// Auto-generated scenario names bake in a UTC timestamp; render it in the
// viewer's local timezone instead so the displayed time matches their clock.
const scenarioLabel = (s) => {
  if (!s?.createdAt) return s?.name || 'Test';
  const prefix = (s.name || 'Test').split(' - ')[0];
  const local = new Date(s.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `${prefix} - ${local}`;
};

const AuthSmashTab = ({ webTarget, isDarkMode, initialSelectedUrls = [], onUrlsConsumed, onManualUrlsChange, onSendToRepeater, onSendToIntruder }) => {
  const [authProfiles, setAuthProfiles] = useState([]);
  const [showHarImport, setShowHarImport] = useState(false);
  const [enrichingHar, setEnrichingHar] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [results, setResults] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [viewMode, setViewMode] = useState('matrix'); // 'matrix' or 'table'
  const [resultsExpanded, setResultsExpanded] = useState(false); // full-screen results panel

  // Esc exits the full-screen results view
  useEffect(() => {
    if (!resultsExpanded) return;
    const onKey = (e) => { if (e.key === 'Escape') setResultsExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [resultsExpanded]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [liveSessionActive, setLiveSessionActive] = useState(false);
  const [liveSessionId, setLiveSessionId] = useState(null);
  const [liveStats, setLiveStats] = useState({ total: 0, enforced: 0, bypassed: 0, partial: 0 });
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [activeProxySessions, setActiveProxySessions] = useState([]); // Phase 8: For auto profile creation
  const pollingRef = useRef(null);
  const lastConsumedUrlsRef = useRef(null);
  const retryCountRef = useRef(0);

  // Default config with localStorage persistence
  const DEFAULT_CONFIG = {
    baselineProfileId: '',
    testProfileIds: [],
    includeUnauthenticated: true,
    // Live mode: replay POST/PUT/PATCH/DELETE too, so we test whether lower-priv
    // profiles can create/modify content in the baseline user's scope. Each endpoint
    // is replayed at most once per session (server-side dedup) to avoid spam loops.
    liveIncludeStateChanging: true,
    endpointSelection: 'all',
    selectedEndpoints: [],
    scopeFilters: {
      includePatterns: [],
      excludePatterns: [],
      parameterFilters: {
        requireParameters: false,
        parameterTypes: [],
        minParameters: 1
      },
      methodFilters: {
        include: [],
        exclude: []
      },
      usePresets: ['exclude_static'] // Multiple presets, default to exclude static
    },
    enforcementDetectors: {
      enforceKeywords: ['unauthorized', 'forbidden', 'access denied', 'login required'],
      bypassKeywords: [],
      enforceStatusCodes: [401, 403, 302],
      bodyLengthThreshold: 50,
      useRegex: false
    }
  };

  // Merge a persisted config blob with defaults (and migrate old fields).
  // NOTE: selectedEndpoints is intentionally NOT restored from localStorage —
  // project URLs live on the backend (webTarget.manualUrls) so they stay bound
  // to their own project instead of leaking across projects.
  const mergeConfig = (parsed) => {
    if (parsed?.scopeFilters?.usePreset && !parsed.scopeFilters?.usePresets) {
      const oldPreset = parsed.scopeFilters.usePreset;
      parsed.scopeFilters.usePresets = oldPreset === 'none' ? [] : [oldPreset];
      delete parsed.scopeFilters.usePreset;
    }
    return {
      ...DEFAULT_CONFIG,
      ...parsed,
      selectedEndpoints: [], // never restored from storage (see note above)
      scopeFilters: {
        ...DEFAULT_CONFIG.scopeFilters,
        ...(parsed?.scopeFilters || {}),
        usePresets: Array.isArray(parsed?.scopeFilters?.usePresets)
          ? parsed.scopeFilters.usePresets
          : DEFAULT_CONFIG.scopeFilters.usePresets
      },
      enforcementDetectors: {
        ...DEFAULT_CONFIG.enforcementDetectors,
        ...(parsed?.enforcementDetectors || {})
      }
    };
  };

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  // Project-bound URLs (from webTarget.manualUrls) — the stable list shown in the selector
  const [projectUrls, setProjectUrls] = useState([]);
  // URLs the user removed from the list (incl. site-map entries) — stay hidden
  const [excludedUrls, setExcludedUrls] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const loadedTargetRef = useRef(null);
  // Mirrors that stay correct within a single commit (avoid stale-closure clobbering)
  const projectUrlsRef = useRef([]);
  const excludedUrlsRef = useRef([]);
  const setProjectUrlsSynced = (urls) => {
    projectUrlsRef.current = urls;
    setProjectUrls(urls);
  };
  const setExcludedUrlsSynced = (urls) => {
    excludedUrlsRef.current = urls;
    setExcludedUrls(urls);
  };

  // Load per-target config + project URLs whenever the target changes
  useEffect(() => {
    if (!webTarget?._id) return;
    let loaded = DEFAULT_CONFIG;
    try {
      const saved = localStorage.getItem(`authsmash-config-${webTarget._id}`);
      if (saved) loaded = mergeConfig(JSON.parse(saved));
    } catch (err) {
      console.error('Failed to load saved config:', err);
    }
    const urls = (webTarget.manualUrls || []).map(u => ({
      method: u.method || 'GET',
      url: u.url,
      source: u.source || 'manual'
    }));
    const excluded = (webTarget.excludedUrls || []).map(u => ({ method: u.method || 'GET', url: u.url }));
    setProjectUrlsSynced(urls);
    setExcludedUrlsSynced(excluded);
    // Default the test selection to all project URLs so pasted URLs are testable immediately
    setConfig({ ...loaded, selectedEndpoints: urls.map(u => ({ method: u.method, url: u.url })) });
    loadedTargetRef.current = webTarget._id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webTarget?._id]);

  // Save config to per-target localStorage (only after it's been loaded for this target)
  useEffect(() => {
    if (!webTarget?._id || loadedTargetRef.current !== webTarget._id) return;
    try {
      localStorage.setItem(`authsmash-config-${webTarget._id}`, JSON.stringify(config));
    } catch (err) {
      console.error('Failed to save config:', err);
    }
  }, [config, webTarget?._id]);

  // Persist the project URL list + excluded list to the backend
  const persistLists = async (nextUrls, nextExcluded) => {
    setProjectUrlsSynced(nextUrls);
    setExcludedUrlsSynced(nextExcluded);
    if (!webTarget?._id) return;
    try {
      const res = await webTargetService.saveManualUrls(
        webTarget._id,
        nextUrls.map(u => ({ method: u.method, url: u.url, source: u.source || 'manual' })),
        nextExcluded.map(u => ({ method: u.method, url: u.url }))
      );
      // Reconcile with the server-cleaned lists (invalids stripped, deduped)
      if (res?.manualUrls) {
        setProjectUrlsSynced(res.manualUrls.map(u => ({
          method: u.method || 'GET', url: u.url, source: u.source || 'manual'
        })));
      }
      if (res?.excludedUrls) {
        setExcludedUrlsSynced(res.excludedUrls.map(u => ({ method: u.method || 'GET', url: u.url })));
      }
      // Keep the parent's webTarget in sync so a remount of this tab doesn't
      // reload a stale manualUrls list and resurrect just-deleted URLs.
      if (onManualUrlsChange) {
        onManualUrlsChange(res?.manualUrls ?? nextUrls, res?.excludedUrls ?? nextExcluded);
      }
    } catch (err) {
      console.error('Failed to save project URLs:', err);
      toast.error('Failed to save URLs to the project');
    }
  };

  // Add pasted/sent URLs to the project list and auto-select them for testing.
  // Also un-excludes them (so a previously-removed URL can be re-added).
  const handleAddUrls = (newUrls) => {
    const current = projectUrlsRef.current || [];
    const existingKeys = new Set(current.map(u => `${u.method} ${u.url}`));
    const additions = newUrls.filter(u => !existingKeys.has(`${u.method} ${u.url}`));
    if (additions.length === 0) {
      toast.info('Those URLs are already in the project');
      return;
    }
    persistLists([...current, ...additions], []);
    setConfig(prev => ({
      ...prev,
      endpointSelection: 'selected',
      selectedEndpoints: [
        ...prev.selectedEndpoints,
        ...additions.map(u => ({ method: u.method, url: u.url }))
      ]
    }));
    toast.success(`Added ${additions.length} URL${additions.length !== 1 ? 's' : ''} to the project`);
  };

  // Enrich the current run with the HAR-captured admin responses as the baseline
  // (recomputes verdicts vs the real data; keeps the live-captured test results).
  const handleEnrichHar = async () => {
    if (!selectedScenario?._id) return;
    if (!window.confirm('Enrich this run from the HAR?\n\n• Baseline becomes the HAR device capture (real admin data).\n• Adds a "HAR admin (replayed here)" column by replaying the admin cookies live from this server.\n• Recomputes verdicts vs the device capture.\n\nYour low-priv test results are preserved (no full re-run).')) return;
    setEnrichingHar(true);
    try {
      const res = await authSmashService.enrichHarBaseline(selectedScenario._id);
      toast.success(
        `HAR enrichment done: ${res.dataEndpoints} data endpoints. Device-capture baseline set, ${res.recomputed} low-priv verdicts recomputed, ${res.replayedHere} admin requests replayed here.` +
        (res.endpointsWithoutHar ? ` (${res.endpointsWithoutHar} had no HAR match)` : '') +
        (res.harBodiesEmpty ? ` — ${res.harBodiesEmpty} HAR bodies empty/capped; re-import for full data.` : '')
      );
      await loadResults(selectedScenario._id);
    } catch (err) {
      toast.error('Enrich from HAR failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setEnrichingHar(false);
    }
  };

  // Copy the checked AuthSmash URLs into the site tree as exchange stubs,
  // attributed to the baseline (admin) profile so they show its color dot.
  const handleCopyToSiteTree = async (items) => {
    if (!items || items.length === 0) return;
    const profileId = config.baselineProfileId || null;
    try {
      const res = await httpExchangeService.createStubs(webTarget._id, items, profileId);
      toast.success(
        `Copied ${res.created} URL${res.created !== 1 ? 's' : ''} to the site tree` +
        (profileId ? ' (attributed to the baseline profile)' : ' — no baseline profile selected, added as anonymous')
      );
    } catch (err) {
      toast.error('Copy to site tree failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // After a HAR import, add its endpoints straight into the AuthSmash list and
  // refresh the profile pickers so the newly-minted admin profile is selectable.
  const handleHarImported = (payload) => {
    if (payload && payload.har && Array.isArray(payload.importedEndpoints) && payload.importedEndpoints.length) {
      handleAddUrls(payload.importedEndpoints);
    }
    authProfileService.getAll(webTarget.project).then(setAuthProfiles).catch(() => {});
  };

  // Remove URLs from the AuthSmash session list only (never touches the site tree).
  const removeUrls = (targets) => {
    if (!targets || targets.length === 0) return;
    const keys = new Set(targets.map(t => `${t.method} ${t.url}`));
    const nextUrls = (projectUrlsRef.current || []).filter(u => !keys.has(`${u.method} ${u.url}`));
    persistLists(nextUrls, []);
    setConfig(prev => ({
      ...prev,
      selectedEndpoints: prev.selectedEndpoints.filter(sel => !keys.has(`${sel.method} ${sel.url}`))
    }));
  };

  const handleDeleteUrl = (target) => removeUrls([target]);
  const handleDeleteUrls = (targets) => removeUrls(targets);

  useEffect(() => {
    if (webTarget?.project) {
      authProfileService.getAll(webTarget.project).then(setAuthProfiles).catch(() => {});
    }
    if (webTarget?._id) {
      loadScenarios();
      loadActiveProxySessions();
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [webTarget]);

  // Handle URLs from "Send to AuthSmash". Each send passes a NEW array reference;
  // we process every new batch (not just the first) and dedupe by reference so a
  // single batch is never consumed twice. The parent clears the list via
  // onUrlsConsumed after each batch, so subsequent sends keep working.
  useEffect(() => {
    if (initialSelectedUrls && initialSelectedUrls.length > 0 && webTarget?._id
        && lastConsumedUrlsRef.current !== initialSelectedUrls) {
      lastConsumedUrlsRef.current = initialSelectedUrls;
      handleAddUrls(
        initialSelectedUrls.map(url => ({ method: 'GET', url, source: 'send_to_authsmash' }))
      );
      if (onUrlsConsumed) onUrlsConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedUrls, onUrlsConsumed, webTarget?._id]);

  // Check for active live sessions on mount or scenario change
  useEffect(() => {
    const checkActiveLiveSession = async () => {
      if (!selectedScenario?._id) return;

      try {
        const statusRes = await authSmashService.getLiveStatus(selectedScenario._id);
        if (statusRes.active) {
          // Found an active session - sync the state
          setLiveSessionActive(true);
          setLiveSessionId(selectedScenario._id);
          setLiveStats({
            total: statusRes.summary?.totalEndpoints || 0,
            enforced: statusRes.summary?.enforced || 0,
            bypassed: statusRes.summary?.bypassed || 0,
            partial: statusRes.summary?.partial || 0
          });
          toast.info('📡 Resumed active live session');

          // Start polling
          if (!pollingRef.current) {
            pollingRef.current = setInterval(async () => {
              try {
                const status = await authSmashService.getLiveStatus(selectedScenario._id);
                if (status.active) {
                  setLiveStats({
                    total: status.summary?.totalEndpoints || 0,
                    enforced: status.summary?.enforced || 0,
                    bypassed: status.summary?.bypassed || 0,
                    partial: status.summary?.partial || 0
                  });
                  loadResults(selectedScenario._id);
                }
              } catch (err) {
                console.error('Live polling error:', err);
              }
            }, 2000);
          }
        }
      } catch (err) {
        // No active session or error - that's fine
        console.log('No active live session');
      }
    };

    checkActiveLiveSession();
  }, [selectedScenario?._id]);

  // Phase 8: Load active proxy sessions for auto profile creation
  const loadActiveProxySessions = async () => {
    try {
      const response = await axios.get(`proxy-sessions?webTargetId=${webTarget._id}`);
      // Ensure response.data is an array (backend might return error object)
      const sessions = Array.isArray(response.data) ? response.data : [];
      const activeSessions = sessions.filter(s => s.status === 'running' || s.isActive);
      setActiveProxySessions(activeSessions);
    } catch (error) {
      console.error('Error loading proxy sessions:', error);
      setActiveProxySessions([]);
    }
  };

  // Load proxy sessions on mount and poll every 10 seconds
  useEffect(() => {
    if (!webTarget?._id) return;

    loadActiveProxySessions(); // Initial load

    const interval = setInterval(loadActiveProxySessions, 10000); // Poll every 10s

    return () => clearInterval(interval);
  }, [webTarget?._id]);

  // The AuthSmash session list = ONLY the user's curated URLs (pasted or sent
  // from the tree). The site map is NOT auto-merged here — deleting a URL only
  // affects this list, never the site tree. This list is INDEPENDENT of the
  // checkbox selection, so unchecking a row never removes it from the list.
  const availableEndpoints = useMemo(() => {
    const combined = [];
    const seen = new Set();
    (projectUrls || []).forEach(u => {
      const m = u.method || 'GET';
      if (!u.url) return;
      const key = `${m} ${u.url}`;
      if (seen.has(key)) return;
      seen.add(key);
      combined.push({ method: m, url: u.url, source: u.source || 'manual' });
    });
    return combined;
  }, [projectUrls]);

  // Phase 8: Handle profile auto-creation
  const handleProfileAutoCreated = async (autoCreatedProfile) => {
    // Show toast notification
    toast.success(
      `✨ Profile auto-created: ${autoCreatedProfile.profileName} (${autoCreatedProfile.role})`,
      {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      }
    );

    // Reload auth profiles to include the new one
    if (webTarget?.project) {
      const profiles = await authProfileService.getAll(webTarget.project);
      setAuthProfiles(profiles);

      // Auto-select as baseline if no baseline selected
      if (!config.baselineProfileId && autoCreatedProfile.profileId) {
        setConfig(prev => ({
          ...prev,
          baselineProfileId: autoCreatedProfile.profileId.toString()
        }));

        toast.info(
          `${autoCreatedProfile.profileName} set as baseline profile`,
          { autoClose: 3000 }
        );
      }
    }
  };

  const loadScenarios = async () => {
    try {
      const data = await authSmashService.getScenarios(webTarget._id);
      setScenarios(data);

      // Auto-resume progress polling if a test is still running (e.g. page was
      // refreshed mid-test or user navigated away and came back).
      if (!pollingRef.current && !running) {
        const runningScenario = data.find(s => s.status === 'running');
        if (runningScenario) {
          setRunning(true);
          pollingRef.current = setInterval(async () => {
            try {
              const s = await authSmashService.getStatus(runningScenario._id);
              setStatus(s);
              if (!s.isRunning && s.status !== 'running') {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
                setRunning(false);
                loadResults(runningScenario._id);
                loadScenarios();
                toast.success('Authorization test completed!');
              }
            } catch {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setRunning(false);
            }
          }, 2000);
        }
      }
    } catch {}
  };

  const loadResults = async (scenarioId) => {
    setLoadingResults(true);
    try {
      console.log('[AuthSmash] Loading results for scenario:', scenarioId);
      const [resultsData, matrixDataRes] = await Promise.all([
        authSmashService.getResults(scenarioId),
        authSmashService.getMatrix(scenarioId)
      ]);
      console.log('[AuthSmash] Matrix data received:', matrixDataRes);
      console.log('[AuthSmash] - Baseline:', matrixDataRes?.baseline);
      console.log('[AuthSmash] - Profiles:', matrixDataRes?.profiles?.length);
      console.log('[AuthSmash] - Endpoints:', matrixDataRes?.endpoints?.length);
      setResults(resultsData);
      setMatrixData(matrixDataRes);
      setSelectedScenario(resultsData.scenario);
    } catch (err) {
      console.error('[AuthSmash] Failed to load results:', err);
      console.error('[AuthSmash] Error details:', err.response?.data || err.message);
      toast.error(`Failed to load test results: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoadingResults(false);
    }
  };

  const handleCellClick = (endpoint, profile, cell) => {
    setSelectedCell({
      endpoint,
      profile,
      cell
    });
  };

  // Re-run a previous test: restore its profiles + endpoint config, then run.
  // Uses the CURRENT profiles (fresh tokens) matched to the snapshot by id.
  const handleRerun = async (scenario) => {
    const cfg = scenario?.authsmashConfig || {};
    const currentIds = new Set(authProfiles.map(p => String(p._id)));
    const baselineId = cfg.baselineProfile?._id ? String(cfg.baselineProfile._id) : '';
    const testIds = (cfg.testProfiles || []).map(p => String(p._id));
    const missing = [];
    if (baselineId && !currentIds.has(baselineId)) missing.push(`baseline "${cfg.baselineProfile?.name}"`);
    testIds.forEach((id, i) => { if (!currentIds.has(id)) missing.push(`"${cfg.testProfiles[i]?.name}"`); });

    // Re-run must test EXACTLY the endpoints the original test used — not the
    // current/full site map. New tests store their endpoints; older ones didn't,
    // so reconstruct the URL list from that test's own results matrix.
    let storedEndpoints = (cfg.selectedEndpoints || []).map(u => (typeof u === 'string' ? { method: 'GET', url: u } : u));
    if (!storedEndpoints.length && scenario?._id) {
      try {
        const matrix = await authSmashService.getMatrix(scenario._id);
        storedEndpoints = (matrix?.endpoints || [])
          .map(e => ({ method: e.method || 'GET', url: e.url || e.path }))
          .filter(e => e.url);
      } catch { /* fall through to warning below */ }
    }

    const restored = {
      ...config,
      baselineProfileId: currentIds.has(baselineId) ? baselineId : '',
      testProfileIds: testIds.filter(id => currentIds.has(id)),
      includeUnauthenticated: cfg.includeUnauthenticated !== false,
      endpointSelection: storedEndpoints.length ? 'selected' : (cfg.endpointSelection || config.endpointSelection),
      selectedEndpoints: storedEndpoints
    };
    setConfig(restored);

    if (missing.length) {
      toast.warning(`Config loaded, but ${missing.length} profile(s) no longer exist: ${missing.join(', ')}. Select replacements (or recreate with valid tokens), then click Start Test.`, { autoClose: 9000 });
      return;
    }
    if (!storedEndpoints.length) {
      toast.warning('This older test did not save its URL list, so re-running could test the whole site map. Profiles are loaded — select the URLs you want and click Start Test.', { autoClose: 10000 });
      return;
    }
    toast.info(`Re-running the original ${storedEndpoints.length} URL(s) with the same profiles (current tokens)…`);
    handleStart(restored);
  };

  // Load a previous test's URLs into the selector above (like "Send to AuthSmash"),
  // WITHOUT running — so the user can add/remove URLs and start a fresh test.
  const handleLoadUrls = async (scenario) => {
    const cfg = scenario?.authsmashConfig || {};
    let urls = (cfg.selectedEndpoints || []).map(u => (typeof u === 'string' ? u : u?.url)).filter(Boolean);
    if (!urls.length && scenario?._id) {
      try {
        const matrix = await authSmashService.getMatrix(scenario._id);
        urls = (matrix?.endpoints || []).map(e => e.url || e.path).filter(Boolean);
      } catch { /* */ }
    }
    if (!urls.length) { toast.warning('This test has no saved URL list to load.'); return; }
    handleAddUrls(urls.map(url => ({ method: 'GET', url, source: 'send_to_authsmash' })));
  };

  const handleStart = async (overrideConfig) => {
    const cfg = overrideConfig && overrideConfig.baselineProfileId !== undefined ? overrideConfig : config;
    if (!cfg.baselineProfileId) {
      toast.error('Please select a baseline profile');
      return;
    }
    if (cfg.testProfileIds.length === 0 && !cfg.includeUnauthenticated) {
      toast.error('Please select at least one test profile or enable unauthenticated testing');
      return;
    }

    setRunning(true);
    try {
      const { scenarioId } = await authSmashService.startTest(webTarget._id, cfg);
      toast.success('Authorization test started!');

      pollingRef.current = setInterval(async () => {
        try {
          const s = await authSmashService.getStatus(scenarioId);
          setStatus(s);
          if (!s.isRunning && s.status !== 'running') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setRunning(false);
            loadResults(scenarioId);
            loadScenarios();
            toast.success('Authorization test completed!');
          }
        } catch {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setRunning(false);
        }
      }, 2000);
    } catch (err) {
      console.error('AuthZ test error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to start authorization test';
      toast.error(`AuthSmash error: ${errorMsg}`);
      setRunning(false);
    }
  };

  const handleCancel = async () => {
    if (selectedScenario?._id) {
      await authSmashService.cancelTest(selectedScenario._id).catch(() => {});
    }
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setRunning(false);
  };

  const handleStartLive = async () => {
    if (!config.baselineProfileId) {
      toast.error('Please select a baseline profile');
      return;
    }
    if (config.testProfileIds.length === 0 && !config.includeUnauthenticated) {
      toast.error('Please select at least one test profile or enable unauthenticated testing');
      return;
    }

    try {
      // Auto-restart proxy to ensure it's ready (fixes issues after deployments)
      if (activeProxySessions.length > 0) {
        const proxySession = activeProxySessions[0];
        try {
          toast.info('Restarting proxy...');
          await proxyService.restart(proxySession._id);
          toast.success('Proxy restarted successfully');
        } catch (proxyErr) {
          console.error('Proxy restart error:', proxyErr);
          toast.warning('Proxy restart failed, continuing anyway...');
        }
      }

      const mode = selectedScenario ? 'append' : 'new';
      console.log('[AuthSmash] Starting Live mode:', mode, 'scenario:', selectedScenario?._id);

      const response = await authSmashService.startLiveSession({
        webTargetId: webTarget._id,
        baselineProfileId: config.baselineProfileId,
        testProfileIds: config.testProfileIds,
        includeUnauthenticated: config.includeUnauthenticated,
        allowStateChangingReplay: config.liveIncludeStateChanging !== false,
        scopeFilters: config.scopeFilters,
        enforcementDetectors: config.enforcementDetectors,
        mode: mode,
        existingScenarioId: selectedScenario?._id
      });

      setLiveSessionActive(true);
      setLiveSessionId(response.sessionId);
      retryCountRef.current = 0; // Reset retry counter on success

      // Load the scenario immediately
      if (response.scenarioId) {
        await loadResults(response.scenarioId);
      }

      const modeMessage = mode === 'append'
        ? `🔴 Live AuthSmash started! Appending to "${selectedScenario.name}"`
        : '🔴 Live AuthSmash started! New test session created';
      toast.success(modeMessage + ' - Browse through proxy to test requests');

      // Poll for live stats
      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await authSmashService.getLiveStatus(response.sessionId);
          if (statusRes.active) {
            setLiveStats({
              total: statusRes.summary?.totalEndpoints || 0,
              enforced: statusRes.summary?.enforced || 0,
              bypassed: statusRes.summary?.bypassed || 0,
              partial: statusRes.summary?.partial || 0
            });
            // Always refresh matrix during live session
            if (statusRes.scenarioId) {
              loadResults(statusRes.scenarioId);
            }
          }
        } catch (err) {
          console.error('Live polling error:', err);
        }
      }, 2000);
    } catch (err) {
      console.error('Start live error:', err);
      const errorMsg = err.response?.data?.error || err.message;

      // If session already active, try to force stop and retry (max 1 retry)
      if (errorMsg.includes('already active') && retryCountRef.current === 0) {
        retryCountRef.current = 1;
        toast.warning('Stopping stuck session and retrying...');
        try {
          // Try to stop the stuck session
          if (selectedScenario?._id) {
            await authSmashService.stopLiveSession(selectedScenario._id).catch(() => {});
          }
          // Wait a moment then retry
          setTimeout(() => {
            handleStartLive();
            retryCountRef.current = 0; // Reset after retry
          }, 1000);
          return;
        } catch (retryErr) {
          console.error('Retry failed:', retryErr);
          retryCountRef.current = 0;
        }
      }

      // Reset retry counter on any error
      retryCountRef.current = 0;

      toast.error('Failed to start live session: ' + errorMsg);
    }
  };

  const handleStopLive = async () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    try {
      await authSmashService.stopLiveSession(liveSessionId);
      toast.info('Live session stopped');
      loadScenarios();
      if (selectedScenario?._id) {
        loadResults(selectedScenario._id);
      }
    } catch (err) {
      console.error('Stop live error:', err);
    }

    setLiveSessionActive(false);
    setLiveSessionId(null);
    setLiveStats({ total: 0, enforced: 0, bypassed: 0, partial: 0 });
  };

  const toggleTestProfile = (id) => {
    setConfig(prev => ({
      ...prev,
      testProfileIds: prev.testProfileIds.includes(id)
        ? prev.testProfileIds.filter(p => p !== id)
        : [...prev.testProfileIds, id]
    }));
  };

  const handleDeleteScenario = async (scenarioId, e) => {
    e.stopPropagation(); // Prevent clicking the row
    if (!window.confirm('Are you sure you want to delete this test? This will also delete all associated exchanges.')) {
      return;
    }
    try {
      await authSmashService.deleteScenario(scenarioId);
      toast.success('Test deleted successfully');
      // Clear selection if deleted scenario was selected
      if (selectedScenario?._id === scenarioId) {
        setSelectedScenario(null);
        setResults(null);
        setMatrixData(null);
      }
      // Reload scenarios list
      loadScenarios();
    } catch (err) {
      console.error('Delete scenario error:', err);
      toast.error(`Failed to delete test: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleNewTest = async () => {
    console.log('[AuthSmash] handleNewTest called');
    console.log('[AuthSmash] - webTarget._id:', webTarget?._id);
    console.log('[AuthSmash] - config:', config);

    if (!webTarget?._id) {
      toast.error('No web target selected');
      return;
    }

    if (!config.baselineProfileId) {
      toast.error('Please select a baseline profile first');
      return;
    }
    if (config.testProfileIds.length === 0 && !config.includeUnauthenticated) {
      toast.error('Please select at least one test profile or enable unauthenticated testing');
      return;
    }

    try {
      console.log('[AuthSmash] Creating blank scenario...');

      // Clear any existing selection to ensure we create NEW
      setSelectedScenario(null);
      setResults(null);
      setMatrixData(null);

      // Create a new blank scenario
      const response = await authSmashService.createBlankScenario(webTarget._id, config);
      console.log('[AuthSmash] Blank scenario created:', response);

      toast.success('✨ New test session created: ' + response.scenario.name);

      // Reload scenarios and select the new one
      console.log('[AuthSmash] Reloading scenarios...');
      await loadScenarios();

      console.log('[AuthSmash] Setting selected scenario:', response.scenario._id);
      setSelectedScenario(response.scenario);

      console.log('[AuthSmash] Loading results...');
      await loadResults(response.scenario._id);

      console.log('[AuthSmash] New test session setup complete');
    } catch (err) {
      console.error('[AuthSmash] Create new test error:', err);
      console.error('[AuthSmash] Error response:', err.response?.data);
      toast.error('Failed to create new test: ' + (err.response?.data?.error || err.message));
    }
  };

  const inputClass = `text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`;
  const labelClass = `text-[10px] font-medium block mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`;

  const roleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      privileged: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      authenticated: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      low_privilege: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      unauthenticated: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[role] || colors.authenticated;
  };

  // Results panel content (view toggle + matrix/table). Reused inline and full-screen.
  const renderResultsPanel = () => (
    <div className="h-full flex flex-col">
      {/* Compact view toggle bar */}
      <div className={`flex items-center justify-end gap-1 px-2 py-1 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <button
          onClick={() => setViewMode('matrix')}
          className={`px-2 py-1 rounded text-xs transition-colors ${
            viewMode === 'matrix'
              ? 'bg-amber-600 text-white'
              : isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
          }`}
          title="Matrix View"
        >
          <FontAwesomeIcon icon={faThLarge} />
        </button>
        <button
          onClick={() => setViewMode('table')}
          className={`px-2 py-1 rounded text-xs transition-colors ${
            viewMode === 'table'
              ? 'bg-amber-600 text-white'
              : isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
          }`}
          title="Table View"
        >
          <FontAwesomeIcon icon={faTable} />
        </button>
        <div className={`w-px h-4 mx-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />
        <button
          onClick={handleEnrichHar}
          disabled={enrichingHar || !selectedScenario?._id}
          className="px-2 py-1 rounded text-xs font-medium flex items-center gap-1.5 bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
          title="Use the HAR-captured admin responses as the baseline and recompute verdicts (keeps the live test results)"
        >
          <FontAwesomeIcon icon={enrichingHar ? faSpinner : faFileImport} spin={enrichingHar} />
          HAR baseline
        </button>
        <div className={`w-px h-4 mx-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />
        <button
          onClick={() => setResultsExpanded(v => !v)}
          className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1.5 transition-colors ${
            resultsExpanded
              ? 'bg-red-600 text-white hover:bg-red-700'
              : isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
          }`}
          title={resultsExpanded ? 'Exit full screen (Esc)' : 'Expand to full screen'}
        >
          <FontAwesomeIcon icon={resultsExpanded ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter} />
          {resultsExpanded && <span>Exit Full Screen</span>}
        </button>
      </div>

      {/* Results view */}
      <div className="flex-1 overflow-auto">
        {viewMode === 'matrix' ? (
          matrixData ? (
            <AuthSmashMatrixTable
              matrix={matrixData}
              onCellClick={handleCellClick}
              onReload={() => selectedScenario?._id && loadResults(selectedScenario._id)}
              onSendToRepeater={onSendToRepeater}
              onSendToIntruder={onSendToIntruder}
              isDarkMode={isDarkMode}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-500">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading matrix...
            </div>
          )
        ) : (
          <AuthSmashResultsTable
            endpoints={results.endpoints || []}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`px-3 py-2 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faShieldAlt} className="text-amber-400" />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            AuthSmash™
          </span>
          {/* Help trigger, horizontally right of the title */}
          <button
            onClick={() => setShowHelp(true)}
            className={`w-4 h-4 flex items-center justify-center rounded-full text-[10px] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-amber-600 hover:text-white'
                : 'bg-gray-200 text-gray-600 hover:bg-amber-500 hover:text-white'
            }`}
            title="How does AuthSmash work?"
            aria-label="How does AuthSmash work?"
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </button>
          {running && <FontAwesomeIcon icon={faSpinner} spin className="text-blue-400 text-xs" />}
          {liveSessionActive && (
            <span className="flex items-center gap-1.5 px-2 py-0.5 bg-green-600 text-white text-xs rounded-full animate-pulse">
              <span className="w-2 h-2 bg-white rounded-full"></span>
              Live Recording
            </span>
          )}
        </div>
      </div>

      {showHelp && <AuthSmashHelpModal isDarkMode={isDarkMode} onClose={() => setShowHelp(false)} />}

      <div className="flex-1 flex overflow-hidden">
        {/* Config panel (left) */}
        <div className={`w-80 border-r overflow-y-auto p-3 space-y-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Duplicate Profile Notifications (Phase 9) */}
          {/* Note: Auto-profile creation toasts now handled globally by GlobalAuthProfileNotifications in WebTestingPage */}
          {activeProxySessions.map(session => (
            <React.Fragment key={session._id}>
              <AuthProfileDuplicateNotificationContainer
                proxySessionId={session._id}
                isDarkMode={isDarkMode}
                onActionComplete={async () => {
                  // Reload auth profiles after duplicate is handled
                  if (webTarget?.project) {
                    const profiles = await authProfileService.getAll(webTarget.project);
                    setAuthProfiles(profiles);
                  }
                }}
              />
            </React.Fragment>
          ))}

          {/* Baseline profile */}
          <div>
            <label className={labelClass}>Baseline Profile (High Privilege)</label>
            <select value={config.baselineProfileId} onChange={e => setConfig(p => ({ ...p, baselineProfileId: e.target.value }))} className={`w-full ${inputClass}`}>
              <option value="">Select baseline...</option>
              {authProfiles.map(p => (
                <option key={p._id} value={p._id}>{p.name} ({p.role})</option>
              ))}
            </select>
          </div>

          {/* Test profiles */}
          <div>
            <label className={labelClass}>Test Profiles (Low Privilege)</label>
            <div className="space-y-1 mt-1">
              {authProfiles
                .filter(p => p._id !== config.baselineProfileId)
                .map(p => (
                  <label key={p._id} className={`flex items-center gap-2 text-xs cursor-pointer px-2 py-1 rounded ${
                    config.testProfileIds.includes(p._id)
                      ? (isDarkMode ? 'bg-amber-900/20' : 'bg-amber-50')
                      : ''
                  }`}>
                    <input
                      type="checkbox"
                      checked={config.testProfileIds.includes(p._id)}
                      onChange={() => toggleTestProfile(p._id)}
                      className="rounded"
                    />
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{p.name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${roleColor(p.role)}`}>{p.role}</span>
                  </label>
                ))}
            </div>
          </div>

          {/* Include unauthenticated */}
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={config.includeUnauthenticated}
              onChange={e => setConfig(p => ({ ...p, includeUnauthenticated: e.target.checked }))}
              className="rounded" />
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Include unauthenticated test</span>
          </label>

          {/* Endpoint selection */}
          <div>
            <label className={labelClass}>Endpoints</label>
            <select value={config.endpointSelection} onChange={e => setConfig(p => ({ ...p, endpointSelection: e.target.value }))} className={`w-full ${inputClass}`}>
              <option value="all">All site map entries ({webTarget?.siteMap?.length || 0})</option>
              <option value="in_scope">In-scope only (from Spider scope settings)</option>
              <option value="selected">Manual selection ({config.selectedEndpoints?.length || 0} selected)</option>
            </select>
            {config.endpointSelection === 'in_scope' && (
              <div className={`mt-1 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                <FontAwesomeIcon icon={faQuestionCircle} className="mr-1" />
                Uses endpoints marked as in-scope during Spider crawling (based on include/exclude patterns in Spider settings)
              </div>
            )}
          </div>

          {/* Manual URL Selection */}
          {config.endpointSelection === 'selected' && (
            <div className={`border rounded p-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
              <div className="flex justify-end mb-1.5">
                <button
                  onClick={() => setShowHarImport(true)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium border ${
                    isDarkMode ? 'bg-teal-900/30 text-teal-300 border-teal-800 hover:bg-teal-900/50' : 'bg-teal-50 text-teal-600 border-teal-200 hover:bg-teal-100'
                  }`}
                  title="Import a browser HAR: adds its URLs here and mints an auth profile from the session cookies"
                >
                  <FontAwesomeIcon icon={faFileImport} className="text-[10px]" />
                  Import HAR
                </button>
              </div>
              <AuthSmashUrlSelector
                endpoints={availableEndpoints}
                selectedEndpoints={config.selectedEndpoints}
                onSelectionChange={(newSelection) => setConfig(p => ({ ...p, selectedEndpoints: newSelection }))}
                onAddUrls={handleAddUrls}
                onDeleteUrl={handleDeleteUrl}
                onDeleteUrls={handleDeleteUrls}
                onCopyToSiteTree={handleCopyToSiteTree}
                isDarkMode={isDarkMode}
              />
            </div>
          )}

          {/* Scope Filters - Show for all modes including manual selection */}
          <div className={`border rounded p-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
            <AuthSmashScopeFilters
              scopeFilters={config.scopeFilters}
              onFiltersChange={(newFilters) => setConfig(p => ({ ...p, scopeFilters: newFilters }))}
              totalEndpoints={
                config.endpointSelection === 'selected'
                  ? config.selectedEndpoints?.length || 0
                  : webTarget?.siteMap?.length || 0
              }
              filteredEndpoints={
                config.endpointSelection === 'selected'
                  ? config.selectedEndpoints?.length || 0
                  : webTarget?.siteMap?.length || 0
              }
              isDarkMode={isDarkMode}
              onReset={() => setConfig(prev => ({ ...prev, scopeFilters: DEFAULT_CONFIG.scopeFilters }))}
            />
          </div>

          {/* Enforcement detectors */}
          <div className={`p-2 rounded border space-y-1.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <div className={`text-[10px] font-medium mb-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Enforcement Detectors
            </div>
            <div>
              <label className={labelClass}>Enforce Status Codes</label>
              <input value={config.enforcementDetectors.enforceStatusCodes.join(', ')}
                onChange={e => setConfig(p => ({
                  ...p, enforcementDetectors: {
                    ...p.enforcementDetectors,
                    enforceStatusCodes: e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
                  }
                }))}
                className={`w-full ${inputClass}`} placeholder="401, 403, 302" />
            </div>
            <div>
              <label className={labelClass}>Enforcement Keywords (one per line)</label>
              <textarea
                value={config.enforcementDetectors.enforceKeywords.join('\n')}
                onChange={e => setConfig(p => ({
                  ...p, enforcementDetectors: {
                    ...p.enforcementDetectors,
                    enforceKeywords: e.target.value.split('\n').filter(s => s.trim())
                  }
                }))}
                rows={3}
                className={`w-full ${inputClass} font-mono`}
                placeholder="unauthorized&#10;forbidden&#10;access denied"
              />
            </div>
            <div>
              <label className={labelClass}>Bypass Keywords (one per line)</label>
              <textarea
                value={config.enforcementDetectors.bypassKeywords.join('\n')}
                onChange={e => setConfig(p => ({
                  ...p, enforcementDetectors: {
                    ...p.enforcementDetectors,
                    bypassKeywords: e.target.value.split('\n').filter(s => s.trim())
                  }
                }))}
                rows={2}
                className={`w-full ${inputClass} font-mono`}
                placeholder="welcome&#10;dashboard&#10;profile"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 text-[10px] cursor-pointer">
                <input type="checkbox" checked={config.enforcementDetectors.useRegex || false}
                  onChange={e => setConfig(p => ({
                    ...p, enforcementDetectors: { ...p.enforcementDetectors, useRegex: e.target.checked }
                  }))}
                  className="rounded" />
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Use Regex for keywords</span>
              </label>
              <div className="flex-1">
                <label className={labelClass}>Body Length Threshold (bytes)</label>
                <input type="number" value={config.enforcementDetectors.bodyLengthThreshold}
                  onChange={e => setConfig(p => ({
                    ...p, enforcementDetectors: { ...p.enforcementDetectors, bodyLengthThreshold: parseInt(e.target.value) || 50 }
                  }))}
                  className={`w-full ${inputClass}`} />
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-2">
            <div className="flex gap-2">
              {!running && !liveSessionActive ? (
                <>
                  <button onClick={handleStart}
                    disabled={!config.baselineProfileId || (config.testProfileIds.length === 0 && !config.includeUnauthenticated)}
                    className="flex-1 px-3 py-2 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
                    <FontAwesomeIcon icon={faPlay} /> Start Test
                  </button>
                  <button onClick={handleStartLive}
                    disabled={!config.baselineProfileId || (config.testProfileIds.length === 0 && !config.includeUnauthenticated)}
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
                    <FontAwesomeIcon icon={faPlay} /> Start Live
                  </button>
                </>
              ) : running ? (
                <button onClick={handleCancel}
                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 flex items-center justify-center gap-1.5">
                  <FontAwesomeIcon icon={faStop} /> Cancel
                </button>
              ) : (
                <button onClick={handleStopLive}
                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 flex items-center justify-center gap-1.5">
                  <FontAwesomeIcon icon={faStop} /> Stop Live
                </button>
              )}
            </div>

            {/* Live: state-changing replay opt-in (shown before start) */}
            {!running && !liveSessionActive && (
              <label className={`flex items-start gap-1.5 text-[10px] cursor-pointer ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                title="When Live is running, also replay POST/PUT/PATCH/DELETE so you can see whether test profiles can create or modify content in the baseline user's scope. Each endpoint is replayed at most once per session.">
                <input type="checkbox"
                  checked={config.liveIncludeStateChanging !== false}
                  onChange={e => setConfig({ ...config, liveIncludeStateChanging: e.target.checked })}
                  className="mt-0.5" />
                <span>
                  <strong>Live:</strong> replay POST/PUT too <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>(tests lower-priv content creation — writes real data, once per endpoint)</span>
                </span>
              </label>
            )}

            {/* Live Stats */}
            {liveSessionActive && (
              <div className={`p-2 rounded text-xs ${isDarkMode ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
                <div className={`font-medium mb-1 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                  📡 Live Recording Active
                </div>
                <div className={`text-[10px] mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                  Total Tests: <strong>{liveStats.total}</strong>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="text-green-500">✓{liveStats.enforced}</span>
                  <span className="text-red-500">⚠{liveStats.bypassed}</span>
                  <span className="text-yellow-500">?{liveStats.partial}</span>
                </div>
              </div>
            )}
          </div>

          {/* Progress */}
          {running && status && (
            <div className={`p-2 rounded text-xs ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                Endpoints: {status.progress?.completedEndpoints || 0} / {status.progress?.totalEndpoints || 0}
              </div>
              <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                Requests: {status.progress?.completedRequests || 0} / {status.progress?.totalRequests || 0}
              </div>
              <div className="w-full bg-gray-700 rounded-full h-1.5 mt-1">
                <div className="bg-amber-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${status.progress?.totalEndpoints ? (status.progress.completedEndpoints / status.progress.totalEndpoints * 100) : 0}%` }} />
              </div>
            </div>
          )}

          {/* New Test Button */}
          <button
            onClick={handleNewTest}
            disabled={running || liveSessionActive || !config.baselineProfileId || (config.testProfileIds.length === 0 && !config.includeUnauthenticated)}
            title={liveSessionActive ? 'Stop the live session first' : (running ? 'A test is already running' : '')}
            className={`w-full px-3 py-2 rounded text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            <FontAwesomeIcon icon={faPlay} /> New Test Session
          </button>

          {/* Previous scenarios */}
          <div>
            <div className={`text-[10px] font-medium mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Previous Tests</div>
            <div className="space-y-1">
              {scenarios.map(s => {
                const isLiveSession = liveSessionActive && selectedScenario?._id === s._id;
                const isDisabled = liveSessionActive && selectedScenario?._id !== s._id;

                return (
                  <div key={s._id}
                    className={`px-2 py-1.5 rounded text-xs relative group ${
                      isDisabled
                        ? 'opacity-40 cursor-not-allowed'
                        : 'cursor-pointer'
                    } ${
                      isLiveSession
                        ? (isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-300')
                        : selectedScenario?._id === s._id
                        ? (isDarkMode ? 'bg-amber-900/20' : 'bg-amber-50')
                        : (isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50')
                    }`}>
                    <div
                      onClick={() => !isDisabled && loadResults(s._id)}
                      className="flex items-center justify-between pr-6"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isLiveSession && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-green-600 text-white text-[9px] rounded-full animate-pulse">
                            <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                            LIVE
                          </span>
                        )}
                        <span className={`truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} title={s.name}>{scenarioLabel(s)}</span>
                      </div>
                      <span className={`text-[9px] ${s.status === 'completed' ? 'text-green-400' : s.status === 'running' ? 'text-blue-400' : 'text-gray-500'}`}>
                        {s.status}
                      </span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (!isDisabled) handleLoadUrls(s); }}
                      className={`absolute right-14 top-2 p-1 rounded transition-opacity ${
                        isDarkMode ? 'hover:bg-teal-900/30 text-teal-400' : 'hover:bg-teal-50 text-teal-600'
                      } ${isDisabled ? 'pointer-events-none opacity-40' : ''}`}
                      title="Load this test's URLs into the selector above (to edit, then run a new test)"
                    >
                      <FontAwesomeIcon icon={faListUl} className="text-[10px]" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (!isDisabled && !running) handleRerun(s); }}
                      className={`absolute right-8 top-2 p-1 rounded transition-opacity ${
                        isDarkMode ? 'hover:bg-blue-900/30 text-blue-400' : 'hover:bg-blue-50 text-blue-600'
                      } ${(isDisabled || running) ? 'pointer-events-none opacity-40' : ''}`}
                      title="Re-run this test with the same profiles (current tokens)"
                    >
                      <FontAwesomeIcon icon={faSync} className="text-[10px]" />
                    </button>
                    <button
                      onClick={(e) => !isDisabled && handleDeleteScenario(s._id, e)}
                      className={`absolute right-2 top-2 p-1 rounded transition-opacity ${
                        isDarkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-600'
                      } ${isDisabled ? 'pointer-events-none' : ''}`}
                      title="Delete test"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                    </button>
                    <div onClick={() => !isDisabled && loadResults(s._id)}>
                      {s.authsmashConfig?.summary && (
                        <div className="flex flex-col gap-0.5 mt-0.5">
                          <div className={`text-[8px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {(() => {
                              const n = s.authsmashConfig?.selectedEndpoints?.length ?? s.progress?.totalEndpoints;
                              return n != null ? `${n} URL${n !== 1 ? 's' : ''} tested · outcomes (per profile/URL):` : 'Outcomes (per profile/URL):';
                            })()}
                          </div>
                          <div className="flex gap-2 text-[10px]" title="Enforced · Bypassed · Review — counted per profile×URL cell, not per URL">
                            <span className="text-green-400" title="Enforced">✓ {s.authsmashConfig.summary.enforced}</span>
                            <span className="text-red-400" title="Bypassed">⚠ {s.authsmashConfig.summary.bypassed}</span>
                            <span className="text-yellow-400" title="Review">? {s.authsmashConfig.summary.partial}</span>
                          </div>
                        </div>
                      )}
                      {/* Profiles used in this test — ★ baseline, ⚠ = no longer exists */}
                      {s.authsmashConfig?.baselineProfile && (() => {
                        const ids = new Set(authProfiles.map(p => String(p._id)));
                        const chip = (p, isBaseline) => {
                          if (!p) return null;
                          const avail = p._id && ids.has(String(p._id));
                          return (
                            <span key={(isBaseline ? 'b' : 't') + p._id + p.name}
                              className={`inline-flex items-center gap-0.5 mr-1 mb-0.5 px-1 py-0.5 rounded ${
                                avail ? (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')
                                      : (isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-600')
                              }`}
                              title={avail ? 'Available in current profiles' : 'No longer exists — pick a replacement before re-running'}>
                              {isBaseline ? '★' : ''}{p.name}{avail ? '' : ' ⚠'}
                            </span>
                          );
                        };
                        return (
                          <div className="mt-1 text-[9px] flex flex-wrap items-center">
                            <span className={`mr-1 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>Profiles:</span>
                            {chip(s.authsmashConfig.baselineProfile, true)}
                            {(s.authsmashConfig.testProfiles || []).map(tp => chip(tp, false))}
                            {s.authsmashConfig.includeUnauthenticated !== false && (
                              <span className={`mr-1 mb-0.5 px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-50 text-gray-500'}`}>+ unauth</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
              {scenarios.length === 0 && (
                <div className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>No previous tests</div>
              )}
            </div>
          </div>
        </div>

        {/* Results panel (right) — hidden here while expanded (shown full-screen below) */}
        <div className="flex-1 overflow-auto">
          {results ? (
            !resultsExpanded && renderResultsPanel()
          ) : loadingResults ? (
            <div className={`flex flex-col items-center justify-center h-full gap-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-amber-500" />
              <span className="text-sm">Loading results…</span>
            </div>
          ) : running && status ? (
            <div className={`flex flex-col items-center justify-center h-full gap-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-amber-500" />
              <div className="text-center">
                <div className="text-sm font-medium mb-2">Authorization test in progress…</div>
                <div className="text-xs mb-1">
                  Endpoints: {status.progress?.completedEndpoints || 0} / {status.progress?.totalEndpoints || 0}
                </div>
                <div className="text-xs mb-2">
                  Requests: {status.progress?.completedRequests || 0} / {status.progress?.totalRequests || 0}
                </div>
                <div className="w-64 bg-gray-700 rounded-full h-2 mx-auto">
                  <div className="bg-amber-500 h-2 rounded-full transition-all"
                    style={{ width: `${status.progress?.totalEndpoints ? (status.progress.completedEndpoints / status.progress.totalEndpoints * 100) : 0}%` }} />
                </div>
                <div className="text-[10px] mt-2 opacity-60">
                  {status.progress?.totalEndpoints ? Math.round(status.progress.completedEndpoints / status.progress.totalEndpoints * 100) : 0}% complete
                </div>
              </div>
            </div>
          ) : (
            <div className={`flex items-center justify-center h-full text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Configure and start an authorization test, or select a previous test.
            </div>
          )}
        </div>

        {/* Full-screen results overlay (above the app nav) */}
        {results && resultsExpanded && (
          <div className={`fixed inset-0 z-[9999] p-3 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className={`h-full rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {renderResultsPanel()}
            </div>
          </div>
        )}

        {/* Cell detail modal */}
        {selectedCell && (
          <AuthSmashCellModal
            endpoint={selectedCell.endpoint}
            profile={selectedCell.profile}
            cell={selectedCell.cell}
            onClose={() => setSelectedCell(null)}
            onSendToRepeater={(id) => { setSelectedCell(null); onSendToRepeater?.(id); }}
            onSendToIntruder={(id) => { setSelectedCell(null); onSendToIntruder?.(id); }}
            isDarkMode={isDarkMode}
          />
        )}

        {showHarImport && (
          <ImportDialog
            webTarget={webTarget}
            isDarkMode={isDarkMode}
            onClose={() => setShowHarImport(false)}
            onImported={handleHarImported}
          />
        )}
      </div>
    </div>
  );
};

export default AuthSmashTab;
